export const TEXT_DIGITAL_FILL_TRIGGER_ID = 'text-digital-fill';

const TEXT_DIGITAL_GRADIENT_SELECTOR = '[data-text-digital-gradient]';
const TEXT_DIGITAL_STRIKETHROUGH_SELECTOR = 's, strike, del';

export const findTextDigitalHandoffWord = (words, handoffWord) =>
  Array.from(words).find((word) => word?.dataset?.word === handoffWord) ||
  null;

export const getTextDigitalGradientElement = (element) => {
  if (typeof element?.querySelector !== 'function') {
    return { element: null, isLegacy: false };
  }

  const legacyGradientElement = element.querySelector(
    TEXT_DIGITAL_GRADIENT_SELECTOR
  );

  if (legacyGradientElement) {
    return { element: legacyGradientElement, isLegacy: true };
  }

  const strongGradientElement = element.querySelector('strong');

  if (!strongGradientElement) {
    return { element: null, isLegacy: false };
  }

  strongGradientElement.setAttribute?.('data-text-digital-gradient', '');

  return { element: strongGradientElement, isLegacy: false };
};

export const findTextDigitalGradientWord = (words, gradientElement) =>
  Array.from(words).find((word) => gradientElement?.contains?.(word.node || word)) || null;

export const findTextDigitalStrikethroughWord = (words, textElement) => {
  if (typeof textElement?.querySelector !== 'function') {
    return null;
  }

  const strikethroughElement = textElement.querySelector(
    TEXT_DIGITAL_STRIKETHROUGH_SELECTOR
  );

  if (!strikethroughElement) {
    return null;
  }

  return (
    Array.from(words).find((word) => strikethroughElement.contains?.(word.node || word)) ||
    null
  );
};

export const getTextDigitalMarkerWhitespaceBoundary = (
  textElement,
  gradientElement
) => {
  if (
    typeof textElement?.querySelector !== 'function' ||
    !gradientElement
  ) {
    return null;
  }

  const markerElement = textElement.querySelector(
    TEXT_DIGITAL_STRIKETHROUGH_SELECTOR
  );
  const whitespaceNode = markerElement?.nextSibling;

  if (
    !markerElement ||
    whitespaceNode?.nodeType !== 3 ||
    !/\s/.test(whitespaceNode.textContent || '') ||
    whitespaceNode.nextSibling !== gradientElement
  ) {
    return null;
  }

  return { markerElement, gradientElement };
};

export const restoreTextDigitalMarkerWhitespace = (boundary) => {
  const { markerElement, gradientElement } = boundary || {};
  const parent = markerElement?.parentNode;

  if (!parent || parent !== gradientElement?.parentNode) {
    return false;
  }

  const nextSibling = markerElement.nextSibling;

  if (nextSibling?.nodeType === 3 && /\s/.test(nextSibling.textContent || '')) {
    return false;
  }

  if (nextSibling !== gradientElement) {
    return false;
  }

  const whitespaceNode = markerElement.ownerDocument?.createTextNode?.(' ');

  if (!whitespaceNode || typeof parent.insertBefore !== 'function') {
    return false;
  }

  parent.insertBefore(whitespaceNode, gradientElement);

  return true;
};

export const resolveTextDigitalHandoffWord = (
  words,
  textElement,
  gradientElement,
  legacyHandoffWord
) =>
  findTextDigitalStrikethroughWord(words, textElement) ||
  (legacyHandoffWord
    ? findTextDigitalHandoffWord(words, legacyHandoffWord)
    : null) ||
  findTextDigitalGradientWord(words, gradientElement);

export const getTextDigitalStaticHandoffRange = (element, gradientElement, legacyHandoffWord) => {
  const document = element.ownerDocument;
  const walker = document.createTreeWalker(element, 4);
  const words = [];
  let node;

  while ((node = walker.nextNode())) {
    for (const match of node.textContent.matchAll(/\S+/g)) {
      words.push({
        node,
        start: match.index,
        end: match.index + match[0].length,
        dataset: { word: match[0] },
      });
    }
  }

  const word = resolveTextDigitalHandoffWord(words, element, gradientElement, legacyHandoffWord);
  if (!word) {
    return null;
  }

  const range = document.createRange();
  range.setStart(word.node, word.start);
  range.setEnd(word.node, word.end);
  return range;
};

export const isTextDigitalHandoffActive = (currentTime, handoffEnd) =>
  Number.isFinite(handoffEnd) && currentTime >= handoffEnd;

export const hasTextDigitalHandoffStateChanged = (previousState, nextState) =>
  previousState !== nextState;

export const getTextDigitalGradientWordStyles = (gradientRect, wordRect) => {
  const values = [
    gradientRect?.left,
    gradientRect?.top,
    gradientRect?.width,
    gradientRect?.height,
    wordRect?.left,
    wordRect?.top,
  ];

  if (
    !values.every(Number.isFinite) ||
    gradientRect.width <= 0 ||
    gradientRect.height <= 0
  ) {
    return null;
  }

  return {
    size: `${gradientRect.width}px ${gradientRect.height}px`,
    position: `${gradientRect.left - wordRect.left}px ${
      gradientRect.top - wordRect.top
    }px`,
  };
};

export const updateTextDigitalGradientWordStyles = (gradientElement) => {
  if (
    typeof gradientElement?.getBoundingClientRect !== 'function' ||
    typeof gradientElement.querySelectorAll !== 'function'
  ) {
    return false;
  }

  const words = Array.from(gradientElement.querySelectorAll('.word'));
  const clearWordStyles = () => {
    words.forEach((word) => {
      word.style?.removeProperty('--text-digital-gradient-size');
      word.style?.removeProperty('--text-digital-gradient-position');
    });

    gradientElement.classList?.toggle(
      'is-text-digital-gradient-positioned',
      false
    );
  };

  if (
    !words.length ||
    words.some(
      (word) =>
        typeof word?.getBoundingClientRect !== 'function' ||
        typeof word.style?.setProperty !== 'function'
    )
  ) {
    clearWordStyles();
    return false;
  }

  const gradientRect = gradientElement.getBoundingClientRect();
  const wordStyles = words.map((word) => ({
    word,
    styles: getTextDigitalGradientWordStyles(
      gradientRect,
      word.getBoundingClientRect()
    ),
  }));

  if (wordStyles.some(({ styles }) => !styles)) {
    clearWordStyles();
    return false;
  }

  wordStyles.forEach(({ word, styles }) => {
    word.style.setProperty('--text-digital-gradient-size', styles.size);
    word.style.setProperty(
      '--text-digital-gradient-position',
      styles.position
    );
  });

  gradientElement.classList?.toggle(
    'is-text-digital-gradient-positioned',
    true
  );

  return true;
};
